"use client";

import { useState, useEffect } from "react";
import {
	Dialog,
	DialogBackdrop,
	DialogPanel,
	DialogTitle,
} from "@headlessui/react";
import { XMarkIcon, CheckCircleIcon } from "@heroicons/react/24/outline";
import { Loader2 } from "lucide-react";
import { useDeviceAndWalletDetection } from "@/hooks/useDeviceAndWalletDetection";
import { usePhantomConnection } from "@/hooks/usePhantomConnection";
import { MYGEOTOKENS_WALLET_DISPLAY_NAME } from "@/constants/wallet-branding";

interface CustomPhantomModalProps {
	isOpen: boolean;
	onClose: () => void;
	onEmailSubmit: (email: string, walletAddress: string) => void;
	isLoading: boolean;
	error: string | null;
}

export function CustomPhantomModal({
	isOpen,
	onClose,
	onEmailSubmit,
	isLoading,
	error,
}: CustomPhantomModalProps) {
	const [email, setEmail] = useState("");
	const [emailError, setEmailError] = useState("");
	const [phase, setPhase] = useState<"email" | "phantom" | "success">("email");
	const [connectedWalletAddress, setConnectedWalletAddress] = useState<
		string | null
	>(null);

	//  ALL HOOKS MUST BE CALLED BEFORE ANY CONDITIONAL RETURNS
	const { isMobile } = useDeviceAndWalletDetection();
	const {
		detectPhantom,
		connectPhantom,
		isConnecting: isPhantomConnecting,
		error: phantomError,
		walletAddress: phantomWalletAddress,
		resetConnection,
	} = usePhantomConnection();

	// Clear form when modal closes
	useEffect(() => {
		if (!isOpen) {
			setEmail("");
			setEmailError("");
			setPhase("email");
			setConnectedWalletAddress(null);
			resetConnection();
		} else {
			setPhase("email");
		}
	}, [isOpen, resetConnection]);

	//  NOW we can do conditional returns after all hooks are called
	// Don't render modal on mobile devices
	if (isMobile) {
		return null;
	}

	// Email validation function
	const validateEmail = (email: string): boolean => {
		const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
		return emailRegex.test(email);
	};

	// Handle email input change
	const handleEmailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		const newEmail = e.target.value;
		setEmail(newEmail);

		// Clear error if user starts typing
		if (emailError && newEmail) {
			setEmailError("");
		}
	};

	// Handle form submission
	const handleSubmit = async (e: React.FormEvent) => {
		e.preventDefault();

		// Phase 1: Email validation and collection
		if (phase === "email") {
			// Reset error state
			setEmailError("");

			// Validate email is not empty
			if (!email.trim()) {
				setEmailError("Email is required");
				return;
			}

			// Validate email format
			if (!validateEmail(email.trim())) {
				setEmailError("Please enter a valid email address");
				return;
			}

			// Check if Phantom is available before proceeding
			if (!detectPhantom()) {
				setEmailError(
					`${MYGEOTOKENS_WALLET_DISPLAY_NAME} browser extension not detected. Please install it and refresh the page.`
				);
				return;
			}

			// Move to Phantom connection phase
			setPhase("phantom");

			try {
				// Connect to Phantom
				const walletAddress = await connectPhantom();
				setConnectedWalletAddress(walletAddress);
				setPhase("success");

				// Call parent callback with both email and wallet address
				onEmailSubmit(email.trim(), walletAddress);
			} catch (error: any) {
				// Error is already handled by usePhantomConnection hook
				// Just log it for debugging
				console.error("Phantom connection failed in modal:", error);
				// Stay in phantom phase to show error and allow retry
			}
		}
	};

	// Handle modal close.
	// Always allow dismissal: if a connection attempt is mid-flight we still
	// reset our local state and let the parent close the modal. Otherwise a
	// failed Phantom request can leave the X button permanently disabled with
	// no way out (Task 5).
	const handleClose = () => {
		setEmail("");
		setEmailError("");
		setPhase("email");
		setConnectedWalletAddress(null);
		resetConnection();
		onClose();
	};

	// Retry Phantom connection
	const retryPhantomConnection = async () => {
		try {
			const walletAddress = await connectPhantom();
			setConnectedWalletAddress(walletAddress);
			setPhase("success");

			// Call parent callback with both email and wallet address
			onEmailSubmit(email.trim(), walletAddress);
		} catch (error: any) {
			console.error("Phantom connection retry failed:", error);
			// Stay in phantom phase to show error and allow another retry
		}
	};

	// Check if submit button should be enabled based on phase
	const isSubmitDisabled = () => {
		if (phase === "email") {
			return (
				!email.trim() ||
				!validateEmail(email.trim()) ||
				isLoading ||
				isPhantomConnecting
			);
		}
		return true; // Disable in other phases
	};

	// Get the current error to display
	const currentError = phase === "phantom" ? phantomError : error || emailError;

	// Determine if we're in a loading state
	const isInLoadingState = isLoading || isPhantomConnecting;

	return (
		<Dialog open={isOpen} onClose={handleClose} className="relative z-[9997]">
			<DialogBackdrop className="fixed inset-0 bg-black/60 backdrop-blur-sm transition-opacity" />

			<div className="fixed inset-0 z-[9997] overflow-y-auto">
				<div className="flex min-h-full items-center justify-center p-4">
					<DialogPanel className="relative transform overflow-hidden rounded-xl bg-[#1A1A1A] border border-gray-700 shadow-2xl transition-all w-full max-w-md">
						{/* Header */}
						<div className="flex items-center justify-between p-6 border-b border-gray-700">
							<DialogTitle className="text-xl font-semibold text-white">
								{phase === "email" && `Connect with ${MYGEOTOKENS_WALLET_DISPLAY_NAME}`}
								{phase === "phantom" && `Connecting ${MYGEOTOKENS_WALLET_DISPLAY_NAME}`}
								{phase === "success" && "Connection Successful"}
							</DialogTitle>
							<button
								onClick={handleClose}
								className="text-gray-400 hover:text-white transition-colors"
								aria-label="Close"
							>
								<XMarkIcon className="w-6 h-6" />
							</button>
						</div>

						{/* Content */}
						<div className="p-6">
							{/* Email Phase */}
							{phase === "email" && (
								<>
									<p className="text-gray-300 text-sm mb-6">
										Enter your email address to connect {MYGEOTOKENS_WALLET_DISPLAY_NAME}.
										This will create a secure account that links your wallets.
									</p>

									<form onSubmit={handleSubmit} className="space-y-4">
										{/* Email Input */}
										<div>
											<label
												htmlFor="email"
												className="block text-sm font-medium text-white mb-2"
											>
												Email Address
											</label>
											<input
												type="email"
												id="email"
												value={email}
												onChange={handleEmailChange}
												disabled={isInLoadingState}
												placeholder="Enter your email address"
												className={`w-full px-4 py-3 rounded-lg bg-gray-800 border text-white placeholder-gray-400 transition-colors focus:outline-none focus:ring-2 focus:ring-mgt-green disabled:opacity-50 disabled:cursor-not-allowed ${
													emailError
														? "border-red-500 focus:border-red-500 focus:ring-red-500"
														: "border-gray-600 focus:border-mgt-green"
												}`}
											/>
											{/* Email validation error */}
											{emailError && (
												<p className="text-red-400 text-sm mt-1">
													{emailError}
												</p>
											)}
										</div>

										{/* Submit Button */}
										<button
											type="submit"
											disabled={isSubmitDisabled()}
											className={`w-full py-3 px-6 rounded-lg font-medium transition-all duration-200 ${
												isSubmitDisabled()
													? "bg-gray-600 text-gray-400 cursor-not-allowed"
													: "bg-mgt-green hover:bg-mgt-green/90 text-black hover:shadow-lg transform hover:scale-[1.02] active:scale-[0.98]"
											} flex items-center justify-center gap-2`}
										>
											Continue with {MYGEOTOKENS_WALLET_DISPLAY_NAME}
										</button>
									</form>

									{/* Help Text */}
									<div className="mt-6 text-center">
										<p className="text-gray-400 text-xs">
											Make sure you have the {MYGEOTOKENS_WALLET_DISPLAY_NAME}{" "}
											browser extension installed and unlocked
										</p>
									</div>
								</>
							)}

							{/* Phantom Connection Phase */}
							{phase === "phantom" && (
								<>
									<div className="text-center">
										<div className="mb-4">
											<div className="w-16 h-16 bg-purple-600 rounded-full flex items-center justify-center mx-auto mb-4">
												{isPhantomConnecting ? (
													<Loader2 className="w-8 h-8 text-white animate-spin" />
												) : (
													<XMarkIcon className="w-8 h-8 text-white" />
												)}
											</div>
										</div>

										{isPhantomConnecting ? (
											<>
												<h3 className="text-lg font-medium text-white mb-2">
													Connecting {MYGEOTOKENS_WALLET_DISPLAY_NAME}...
												</h3>
												<p className="text-gray-300 text-sm mb-4">
													Please approve the connection in your wallet popup.
												</p>
											</>
										) : (
											<>
												<h3 className="text-lg font-medium text-white mb-2">
													Connection Failed
												</h3>
												<p className="text-gray-300 text-sm mb-4">
													We could not connect to {MYGEOTOKENS_WALLET_DISPLAY_NAME}.
												</p>
											</>
										)}

										{/* Show retry button if there's an error and not connecting */}
										{phantomError && !isPhantomConnecting && (
											<button
												onClick={retryPhantomConnection}
												className="bg-purple-600 hover:bg-purple-700 text-white px-4 py-2 rounded-lg transition-colors duration-200 text-sm font-medium flex items-center gap-2 mx-auto"
											>
												Try Again
											</button>
										)}
									</div>
								</>
							)}

							{/* Success Phase */}
							{phase === "success" && (
								<>
									<div className="text-center">
										<div className="w-16 h-16 bg-green-600 rounded-full flex items-center justify-center mx-auto mb-4">
											<CheckCircleIcon className="w-8 h-8 text-white" />
										</div>

										<h3 className="text-lg font-medium text-white mb-2">
											Successfully Connected!
										</h3>
										<p className="text-gray-300 text-sm mb-4">
											{MYGEOTOKENS_WALLET_DISPLAY_NAME} has been connected to your
											account.
										</p>

										{connectedWalletAddress && (
											<div className="bg-gray-800 rounded-lg p-3 mb-4">
												<p className="text-xs text-gray-400 mb-1">
													Wallet Address:
												</p>
												<p className="text-sm text-white font-mono">
													{`${connectedWalletAddress.slice(
														0,
														6
													)}...${connectedWalletAddress.slice(-4)}`}
												</p>
											</div>
										)}

										<p className="text-green-400 text-sm">
											You can now close this window and continue using the app.
										</p>
									</div>
								</>
							)}

							{/* Error Display for any phase */}
							{currentError && (
								<div className="bg-red-500/20 border border-red-500/30 rounded-lg p-3 mt-4">
									<p className="text-red-400 text-sm">{currentError}</p>
								</div>
							)}
						</div>
					</DialogPanel>
				</div>
			</div>
		</Dialog>
	);
}
